import React, { useMemo, useCallback } from 'react';
import { Alert, Collapse } from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { useUsersyncStatisticsData } from '../../../hooks/useUsersyncStatisticsData';
import { type UsersyncStatisticsTableRow } from '../../../types/usersyncStatistics';
import { UsersyncStatisticsFiltersComponent } from './components/UsersyncStatisticsFilters';
import { UsersyncStatisticsTable } from './components/UsersyncStatisticsTable';
import { useExportUsersyncStatisticsCsv } from './hooks/useExportUsersyncStatisticsCsv';

type TableChangeHandler = NonNullable<TableProps<UsersyncStatisticsTableRow>['onChange']>;

export const UsersyncStatisticsPage: React.FC = () => {
  const {
    pendingFilters,
    appliedFilters,
    updatePendingFilters,
    applyFilters,
    hasUnappliedChanges,
    tableData,
    loading,
    error,
    shouldPaginate,
    pagination,
    totalCount,
    pageSizeOptions,
    changePage,
    changeSort,
    order,
  } = useUsersyncStatisticsData();
  const { exportCsv } = useExportUsersyncStatisticsCsv();

  const pageSizeOptionsAsString = useMemo(() => pageSizeOptions.map((size) => size.toString()), [pageSizeOptions]);

  const tablePaginationConfig = useMemo<false | TablePaginationConfig>(() => {
    if (!shouldPaginate) {
      return false;
    }

    return {
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: totalCount,
      showSizeChanger: true,
      hideOnSinglePage: true,
      pageSizeOptions: pageSizeOptionsAsString,
    };
  }, [shouldPaginate, pagination.current, pagination.pageSize, totalCount, pageSizeOptionsAsString]);

  const handleTableChange = useCallback<TableChangeHandler>(
    (tablePagination, _filters, sorter) => {
      const normalizedSorter = Array.isArray(sorter)
        ? (sorter[0] as SorterResult<UsersyncStatisticsTableRow> | undefined)
        : ((sorter as SorterResult<UsersyncStatisticsTableRow>) ?? undefined);

      const sorterColumnKey = (normalizedSorter?.columnKey ?? normalizedSorter?.field) as string | undefined;
      const sorterOrder =
        normalizedSorter?.order === 'ascend' ? 'ASC' : normalizedSorter?.order === 'descend' ? 'DESC' : undefined;

      const sortChanged =
        sorterColumnKey && sorterColumnKey !== 'index'
          ? sorterOrder
            ? sorterColumnKey !== order.by || sorterOrder !== order.direction
            : Boolean(order.by)
          : false;

      if (sortChanged) {
        const nextPage = shouldPaginate ? (tablePagination?.current ?? 1) : undefined;
        const nextPageSize = shouldPaginate ? (tablePagination?.pageSize ?? pagination.pageSize) : undefined;

        if (sorterColumnKey && sorterColumnKey !== 'index' && sorterOrder) {
          changeSort({
            columnKey: sorterColumnKey,
            direction: sorterOrder,
            page: nextPage,
            pageSize: nextPageSize,
          });
        } else {
          changeSort({
            page: nextPage,
            pageSize: nextPageSize,
          });
        }
        return;
      }

      if (shouldPaginate && tablePagination && typeof tablePagination === 'object') {
        const nextPage = tablePagination.current ?? 1;
        const nextPageSize = tablePagination.pageSize ?? pagination.pageSize;

        if (nextPage !== pagination.current || nextPageSize !== pagination.pageSize) {
          changePage(nextPage, nextPageSize);
        }
      }
    },
    [shouldPaginate, changePage, changeSort, pagination.current, pagination.pageSize, order.by, order.direction]
  );

  return (
    <div className="xchange-page">
      <UsersyncStatisticsFiltersComponent
        filters={pendingFilters}
        onFiltersChange={updatePendingFilters}
        onApplyFilters={applyFilters}
        loading={loading}
        hasUnappliedChanges={hasUnappliedChanges}
        onExportCsv={() =>
          exportCsv({
            filters: appliedFilters,
            order,
            pagination,
            shouldPaginate,
          })
        }
        exportDisabled={!tableData || tableData.length === 0}
      />

      <Collapse
        size="small"
        style={{ marginBottom: '16px' }}
        items={[
          {
            key: 'usersync-metrics-guide',
            label: 'Read Metrics Guide',
            children: (
              <div>
                <div>
                  <strong>Initial Requests:</strong> total number of incoming `/user-sync` requests accepted by the
                  service.
                </div>
                <div>
                  <strong>Eligible:</strong> requests that reached the `eligible_for_sync` stage, meaning the user was
                  considered syncable for a specific SSP/DSP pair.
                </div>
                <div>
                  <strong>Generated:</strong> total `sync_attempt_generated` events, when the sync URL was actually
                  added to the response HTML.
                </div>
                <div>
                  <strong>Callbacks:</strong> total `callback_received` events returned from partner sync flows.
                </div>
                <div>
                  <strong>Stored:</strong> total `mapping_stored` events where the usersync mapping was persisted
                  successfully.
                </div>
                <div>
                  <strong>Drops:</strong> total `drop` events recorded for validation, dedup, config, or storage
                  failures.
                </div>
                <div>
                  <strong>Callback Rate:</strong> share of generated sync attempts that resulted in callbacks.
                  <br />
                  Formula: <code>Callbacks / Generated * 100</code>
                </div>
                <div>
                  <strong>Store Rate:</strong> share of callbacks that resulted in persisted mappings.
                  <br />
                  Formula: <code>Stored / Callbacks * 100</code>
                </div>
                <div>
                  <strong>End-to-End:</strong> share of eligible events that ended as stored mappings.
                  <br />
                  Formula: <code>Stored / Eligible * 100</code>
                </div>
                <div>
                  <strong>Drop Rate:</strong> share of eligible events that ended in a drop event.
                  <br />
                  Formula: <code>Drops / Eligible * 100</code>
                </div>
              </div>
            ),
          },
        ]}
      />

      {error && <Alert title="Error" description={error} type="error" showIcon style={{ marginBottom: '16px' }} />}

      <UsersyncStatisticsTable
        data={tableData}
        loading={loading}
        filters={appliedFilters}
        pagination={tablePaginationConfig}
        onTableChange={handleTableChange}
        sortState={order}
      />
    </div>
  );
};
